(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const instances = new WeakMap();

  function initAnnouncement(root = document) {
    root.querySelectorAll('[data-es-announcement]').forEach((announcement) => {
      if (instances.has(announcement)) return;
      const messages = [...announcement.querySelectorAll('.es-announcement__message')];
      const speed = Number(announcement.dataset.rotationSpeed) || 5000;
      let activeIndex = 0;
      let timer = null;

      const show = (index) => {
        messages.forEach((message, messageIndex) => {
          message.classList.toggle('is-active', messageIndex === index);
        });
      };

      if (!reduceMotion && messages.length > 1) {
        timer = window.setInterval(() => {
          activeIndex = (activeIndex + 1) % messages.length;
          show(activeIndex);
        }, speed);
      }

      instances.set(announcement, {
        destroy() {
          if (timer) window.clearInterval(timer);
        }
      });
    });
  }

  function initHeader(root = document) {
    root.querySelectorAll('[data-es-header]').forEach((header) => {
      if (instances.has(header)) return;
      const backdrop = document.querySelector('[data-es-mega-backdrop]');
      const triggers = [...header.querySelectorAll('[data-es-mega-trigger]')];
      const panels = [...document.querySelectorAll('[data-es-mega-panel]')];
      const drawer = document.querySelector('[data-es-mobile-drawer]');
      const announcement = document.querySelector('.es-announcement');
      const openButton = header.querySelector('[data-es-mobile-open]');
      const closeButton = drawer?.querySelector('[data-es-mobile-close]');
      let timer;

      const syncHeaderOffset = () => {
        document.documentElement.style.setProperty(
          '--es-live-announcement-height',
          `${Math.round(announcement?.getBoundingClientRect().height || 0)}px`
        );
      };
      syncHeaderOffset();
      window.addEventListener('resize', syncHeaderOffset, { passive: true });

      const closeMega = () => {
        clearTimeout(timer);
        triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
        panels.forEach((item) => item.classList.remove('is-open'));
        backdrop?.classList.remove('is-open');
        header.classList.remove('menu-is-open');
      };

      const openMega = (trigger) => {
        clearTimeout(timer);
        const id = trigger.dataset.esMegaTrigger;
        triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
        panels.forEach((item) => item.classList.toggle('is-open', item.dataset.esMegaPanel === id));
        backdrop?.classList.add('is-open');
        header.classList.add('menu-is-open');
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => openMega(trigger));
        trigger.addEventListener('focus', () => openMega(trigger));
        trigger.addEventListener('click', () => {
          if (trigger.getAttribute('aria-expanded') === 'true') closeMega();
          else openMega(trigger);
        });
        trigger.addEventListener('mouseleave', () => { timer = setTimeout(closeMega, 180); });
      });
      panels.forEach((panel) => {
        panel.addEventListener('mouseenter', () => clearTimeout(timer));
        panel.addEventListener('mouseleave', () => { timer = setTimeout(closeMega, 100); });
      });
      backdrop?.addEventListener('click', closeMega);

      const closeDrawer = () => {
        drawer?.classList.remove('is-open');
        backdrop?.classList.remove('is-open');
        document.body.style.overflow = '';
      };
      openButton?.addEventListener('click', () => {
        drawer?.classList.add('is-open');
        backdrop?.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });
      closeButton?.addEventListener('click', closeDrawer);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeMega();
          closeDrawer();
        }
      });

      instances.set(header, {
        destroy() {
          window.removeEventListener('resize', syncHeaderOffset);
        }
      });
    });
  }

  function initVideoHero(root = document) {
    root.querySelectorAll('[data-es-video-hero]').forEach((section) => {
      if (instances.has(section)) return;
      const video = section.querySelector('[data-es-scrub-video]');
      const sequence = section.querySelector('[data-es-scroll-sequence]');
      const sequenceData = section.querySelector('[data-es-sequence-frames]');
      const progress = section.querySelector('[data-es-video-progress]');
      const siteHeader = document.querySelector('[data-es-header]');
      const sectionWrapper = section.closest('.shopify-section') || section.parentElement || section;
      const triggerId = `es-video-hero-${section.dataset.esSectionId || 'homepage'}`;
      window.ScrollTrigger?.getById?.(triggerId)?.kill(true);
      if ((!video && !sequence) || reduceMotion || !window.gsap || !window.ScrollTrigger) {
        video?.pause();
        const onScroll = () => siteHeader?.classList.toggle('is-solid', window.scrollY > window.innerHeight - 150);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        instances.set(section, {
          destroy() {
            window.removeEventListener('scroll', onScroll);
          }
        });
        return;
      }

      let frameUrls = [];
      let frameCache = [];
      let renderedFrame = 0;
      if (sequence && sequenceData) {
        try {
          frameUrls = JSON.parse(sequenceData.textContent);
          frameCache = frameUrls.map((src) => {
            const image = new Image();
            image.decoding = 'async';
            image.src = src;
            return image;
          });
        } catch (_) {
          frameUrls = [];
        }
      }

      const renderSequenceFrame = (scrollProgress) => {
        if (!sequence || !frameUrls.length) return;
        const index = Math.min(
          frameUrls.length - 1,
          Math.max(0, Math.round(scrollProgress * (frameUrls.length - 1)))
        );
        if (index === renderedFrame) return;
        renderedFrame = index;
        sequence.src = frameCache[index]?.src || frameUrls[index];
      };

      let duration = 5;
      let targetTime = 0;
      let raf;
      const render = () => {
        if (video && video.readyState >= 1 && Math.abs(video.currentTime - targetTime) > .015) {
          video.currentTime = targetTime;
        }
        raf = requestAnimationFrame(render);
      };
      const setDuration = () => {
        if (!video) return;
        if (Number.isFinite(video.duration)) duration = video.duration;
        video.pause();
        video.currentTime = 0;
      };
      if (video) {
        video.addEventListener('loadedmetadata', setDuration, { once: true });
        setDuration();
        render();
      }

      const trigger = ScrollTrigger.create({
        id: triggerId,
        trigger: sectionWrapper,
        start: 'top top',
        end: () => {
          const baseDistance = Number(section.dataset.scrollDistance || 2300);
          return `+=${Math.round(baseDistance * (window.matchMedia('(max-width: 749px)').matches ? .5 : 1))}`;
        },
        pin: sectionWrapper,
        pinSpacing: true,
        anticipatePin: 1,
        scrub: .65,
        refreshPriority: 20,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (video) targetTime = self.progress * Math.max(.1, duration - .04);
          else renderSequenceFrame(self.progress);
          if (progress) progress.style.transform = `scaleX(${self.progress})`;
          siteHeader?.classList.toggle('is-solid', self.progress >= 1);
        },
        onLeave: () => siteHeader?.classList.add('is-solid'),
        onEnterBack: () => siteHeader?.classList.remove('is-solid'),
        onLeaveBack: () => siteHeader?.classList.remove('is-solid')
      });

      instances.set(section, {
        destroy() {
          if (raf) cancelAnimationFrame(raf);
          trigger.kill();
        }
      });
    });
  }

  function initCollection(root = document) {
    root.querySelectorAll('[data-es-collection]').forEach((section) => {
      if (instances.has(section)) return;
      const track = section.querySelector('[data-es-collection-track]');
      const progress = section.querySelector('[data-es-collection-progress]');
      const sectionWrapper = section.closest('.shopify-section') || section.parentElement || section;
      const triggerId = `es-collection-${section.dataset.esSectionId || 'homepage'}`;
      window.ScrollTrigger?.getById?.(triggerId)?.kill(true);
      if (!track || reduceMotion || !window.gsap || !window.ScrollTrigger || window.innerWidth < 750) {
        instances.set(section, { destroy() {} });
        return;
      }
      const headerOffset = () => {
        const announcement = document.querySelector('.es-announcement');
        const siteHeader = document.querySelector('[data-es-header]');
        if (window.innerWidth < 750) return 0;
        return Math.round(
          (announcement?.getBoundingClientRect().height || 0) +
          (siteHeader?.getBoundingClientRect().height || 0)
        );
      };
      const distance = () => Math.max(1, track.scrollWidth - section.clientWidth);
      const startX = () => section.clientWidth;
      const endX = () => -distance();
      const travel = () => Math.max(1, startX() - endX());
      const tween = gsap.fromTo(track, { x: () => section.clientWidth }, {
        x: endX,
        ease: 'none',
        immediateRender: true,
        scrollTrigger: {
          id: triggerId,
          trigger: sectionWrapper,
          start: () => `top ${headerOffset()}px`,
          end: () => `+=${Math.round(travel())}`,
          pin: sectionWrapper,
          pinSpacing: true,
          anticipatePin: 1,
          scrub: .85,
          preventOverlaps: 'pinned-story',
          fastScrollEnd: true,
          invalidateOnRefresh: true,
          refreshPriority: 10,
          onUpdate: (self) => {
            if (progress) progress.style.transform = `scaleX(${self.progress})`;
          }
        }
      });
      instances.set(section, { destroy: () => tween.kill() });
    });
  }

  function initWhy(root = document) {
    root.querySelectorAll('[data-es-why]').forEach((section) => {
      if (instances.has(section)) return;
      if (reduceMotion || !window.gsap || !window.ScrollTrigger) {
        instances.set(section, { destroy() {} });
        return;
      }
      const animations = [
        gsap.from(section.querySelector('[data-es-why-image]'), {
          y: 42, clipPath: 'inset(0 0 100% 0)', duration: 1.05, ease: 'power3.out',
          scrollTrigger: { trigger: section, start: 'top 76%', once: true }
        }),
        gsap.from(section.querySelector('[data-es-why-proof]'), {
          y: 54, opacity: 0, duration: .75, delay: .25, ease: 'power3.out',
          scrollTrigger: { trigger: section, start: 'top 76%', once: true }
        }),
        gsap.from(section.querySelectorAll('[data-es-why-copy] > *'), {
          x: 42, opacity: 0, stagger: .11, duration: .75, ease: 'power3.out',
          scrollTrigger: { trigger: section, start: 'top 74%', once: true }
        })
      ];
      instances.set(section, { destroy: () => animations.forEach((animation) => animation.kill()) });
    });
  }

  function initProfileFinder(root = document) {
    root.querySelectorAll('[data-es-profile-finder]').forEach((section) => {
      if (instances.has(section)) return;
      if (reduceMotion || !window.gsap || !window.ScrollTrigger) {
        instances.set(section, { destroy() {} });
        return;
      }
      const animations = [
        gsap.from(section.querySelectorAll('.es-profile-finder__header > *'), {
          y: 28,
          opacity: 0,
          stagger: .1,
          duration: .7,
          ease: 'power3.out',
          scrollTrigger: { trigger: section, start: 'top 78%', once: true }
        }),
        gsap.from(section.querySelectorAll('.es-profile-card'), {
          y: 34,
          opacity: 0,
          stagger: .055,
          duration: .62,
          ease: 'power3.out',
          scrollTrigger: { trigger: section.querySelector('.es-profile-finder__grid'), start: 'top 82%', once: true }
        })
      ];
      instances.set(section, { destroy: () => animations.forEach((animation) => animation.kill()) });
    });
  }

  function initBespoke(root = document) {
    root.querySelectorAll('[data-es-bespoke]').forEach((section) => {
      if (instances.has(section)) return;
      const media = section.querySelector('.es-bespoke__media');
      if (reduceMotion || !window.gsap || !window.ScrollTrigger) {
        instances.set(section, { destroy() {} });
        return;
      }
      const animations = [
        gsap.from(section.querySelectorAll('.es-bespoke__copy > *'), {
          y: 26,
          opacity: 0,
          stagger: .08,
          duration: .68,
          ease: 'power3.out',
          scrollTrigger: { trigger: section, start: 'top 78%', once: true }
        })
      ];
      if (media) {
        animations.push(gsap.to(media, {
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: .7 }
        }));
      }
      instances.set(section, { destroy: () => animations.forEach((animation) => animation.kill()) });
    });
  }

  function initFooter(root = document) {
    root.querySelectorAll('[data-es-footer]').forEach((section) => {
      if (instances.has(section)) return;
      if (reduceMotion || !window.gsap || !window.ScrollTrigger) {
        instances.set(section, { destroy() {} });
        return;
      }
      const animation = gsap.from(section.querySelectorAll('.es-footer__top > *'), {
        y: 24,
        opacity: 0,
        stagger: .07,
        duration: .62,
        ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 84%', once: true }
      });
      instances.set(section, { destroy: () => animation.kill() });
    });
  }

  function initEditorialPages(root = document) {
    root.querySelectorAll('[data-es-editorial-page]').forEach((section) => {
      if (instances.has(section)) return;
      if (reduceMotion || !window.gsap || !window.ScrollTrigger) {
        instances.set(section, { destroy() {} });
        return;
      }

      const animations = [];
      section.querySelectorAll('[data-es-reveal-group]').forEach((group) => {
        const children = [...group.children];
        if (!children.length) return;
        animations.push(gsap.from(children, {
          y: 26,
          opacity: 0,
          stagger: .08,
          duration: .68,
          ease: 'power3.out',
          scrollTrigger: { trigger: group, start: 'top 84%', once: true }
        }));
      });

      const cards = section.querySelectorAll('[data-es-reveal-card]');
      if (cards.length) {
        animations.push(gsap.from(cards, {
          y: 34,
          opacity: 0,
          stagger: .09,
          duration: .72,
          ease: 'power3.out',
          scrollTrigger: { trigger: cards[0].parentElement || section, start: 'top 84%', once: true }
        }));
      }

      section.querySelectorAll('[data-es-reveal-media]').forEach((media) => {
        animations.push(gsap.from(media, {
          y: 32,
          opacity: 0,
          duration: .82,
          ease: 'power3.out',
          scrollTrigger: { trigger: media, start: 'top 86%', once: true }
        }));
      });

      instances.set(section, { destroy: () => animations.forEach((animation) => animation.kill()) });
    });
  }

  function initCommerceReveals(root = document) {
    root.querySelectorAll('[data-es-catalog-page],[data-es-product]').forEach((section) => {
      if (instances.has(section)) return;
      if (reduceMotion || !window.gsap || !window.ScrollTrigger) {
        instances.set(section, { destroy() {} });
        return;
      }
      const animations = [];
      const cards = section.querySelectorAll('.es-product-card,.related-product');
      if (cards.length) {
        animations.push(gsap.from(cards, {
          opacity: 0,
          duration: .38,
          ease: 'power3.out',
          scrollTrigger: { trigger: cards[0].parentElement || section, start: 'top 88%', once: true }
        }));
      }
      const productColumns = section.querySelectorAll('.product-gallery,.product-information');
      if (productColumns.length) {
        animations.push(gsap.from(productColumns, {
          y: 28,
          opacity: 0,
          stagger: .12,
          duration: .72,
          ease: 'power3.out'
        }));
      }
      instances.set(section, { destroy: () => animations.forEach((animation) => animation.kill()) });
    });
  }

  function init(root = document) {
    if (window.gsap && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
    initAnnouncement(root);
    initHeader(root);
    initVideoHero(root);
    initCollection(root);
    initWhy(root);
    initProfileFinder(root);
    initBespoke(root);
    initEditorialPages(root);
    initCommerceReveals(root);
    initFooter(root);
    window.ScrollTrigger?.refresh();
  }

  function destroy(root) {
    root.querySelectorAll('[data-es-announcement],[data-es-header],[data-es-video-hero],[data-es-collection],[data-es-why],[data-es-profile-finder],[data-es-bespoke],[data-es-editorial-page],[data-es-catalog-page],[data-es-product],[data-es-footer]').forEach((element) => {
      instances.get(element)?.destroy?.();
      instances.delete(element);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
